import {
  BarChart3,
  FileUp,
  FolderOpen,
  Link as LinkIcon,
  LogOut,
  Menu,
  Users,
} from "lucide-react";
import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Logo } from "./Logo";
import { useCurrentCompany, useCurrentUser, useSession } from "../../services/SessionContext";
import { cn } from "../../lib/utils";

const navItems = [
  { to: "/app/dashboard", label: "Dashboard", icon: BarChart3 },
  { to: "/app/uploads/new", label: "Upload", icon: FileUp },
  { to: "/app/repository", label: "Repository", icon: FolderOpen },
  { to: "/app/shared", label: "Shared Links", icon: LinkIcon },
  { to: "/app/team", label: "Team", icon: Users },
];

export function AppShell() {
  const [open, setOpen] = useState(false);
  const { signOut } = useSession();
  const user = useCurrentUser();
  const company = useCurrentCompany();
  const navigate = useNavigate();

  function handleSignOut() {
    signOut();
    navigate("/");
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Logo to="/app/dashboard" />
          <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    "inline-flex min-h-10 items-center gap-2 rounded-md px-3 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-navy-900",
                    isActive && "bg-navy-50 text-navy-900",
                  )
                }
              >
                <item.icon className="h-4 w-4" aria-hidden />
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden items-center gap-3 md:flex">
            <div className="text-right">
              <p className="text-sm font-semibold text-navy-900">{user?.name ?? user?.email}</p>
              <p className="text-xs text-slate-500">{company?.name ?? "No workspace"}</p>
            </div>
            <Button aria-label="Sign out" size="icon" variant="ghost" onClick={handleSignOut}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
          <Button
            aria-label="Open navigation"
            className="md:hidden"
            size="icon"
            variant="ghost"
            onClick={() => setOpen((value) => !value)}
          >
            <Menu className="h-5 w-5" />
          </Button>
        </div>
        {open ? (
          <div className="border-t border-slate-200 bg-white px-4 py-3 md:hidden">
            <nav className="grid gap-1" aria-label="Mobile primary">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="flex min-h-11 items-center gap-3 rounded-md px-3 text-sm font-medium text-slate-700 hover:bg-slate-100"
                  onClick={() => setOpen(false)}
                >
                  <item.icon className="h-4 w-4" aria-hidden />
                  {item.label}
                </Link>
              ))}
              <button
                className="flex min-h-11 items-center gap-3 rounded-md px-3 text-left text-sm font-medium text-slate-700 hover:bg-slate-100"
                onClick={handleSignOut}
              >
                <LogOut className="h-4 w-4" aria-hidden />
                Sign out
              </button>
            </nav>
          </div>
        ) : null}
      </header>
      <main className="mx-auto max-w-7xl px-4 py-5 sm:px-6 lg:px-8">
        <Outlet />
      </main>
      <footer className="mx-auto max-w-7xl px-4 pb-24 pt-2 text-sm text-slate-500 sm:px-6 md:pb-6 lg:px-8">
        <p>© 2026 Sprinkler Nerd. BaselineBoard is a Sprinkler Nerd product.</p>
      </footer>
      <nav
        className="fixed inset-x-0 bottom-0 z-20 grid grid-cols-5 border-t border-slate-200 bg-white md:hidden"
        aria-label="Quick navigation"
      >
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cn(
                "flex min-h-16 flex-col items-center justify-center gap-1 text-xs font-medium text-slate-600",
                isActive && "text-navy-900",
              )
            }
          >
            <item.icon className="h-5 w-5" aria-hidden />
            {item.label.split(" ")[0]}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
